import type { KeyboardEvent } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import { CloseIcon, colors } from '@truvhq/ui';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import ArrowUpIcon from './icons/arrow-up.svg?react';

type ChatComposerProps = {
  isStreaming: boolean;
  onSend: (text: string) => void;
  onStop: () => void;
};

// The theme greys out every adornment icon button from `.MuiOutlinedInput-root
// .MuiInputAdornment-root`, which out-specifies an sx on the button — so the colour goes on the
// icon, where nothing can inherit over it. `hover` isn't on the MUI palette, only in `colors`.
const sendButtonSx = (isArmed: boolean) =>
  ({
    flex: 'none',
    width: 32,
    height: 32,
    borderRadius: '8px',
    backgroundColor: isArmed ? colors.primary.main : 'transparent',
    '& svg': { color: isArmed ? colors.white : colors.grey60 },
    '&:hover': { backgroundColor: isArmed ? colors.primary.hover : colors.grey20 },
  }) as const;

// trim().min(1) gates empty / whitespace-only input.
const schema = z.object({ message: z.string().trim().min(1) });
type FormValues = z.infer<typeof schema>;

export function ChatComposer({ isStreaming, onSend, onStop }: ChatComposerProps) {
  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: { message: '' },
  });

  const submit = handleSubmit(({ message }) => {
    onSend(message.trim());
    reset({ message: '' });
  });

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    // Enter sends; Shift+Enter inserts a newline. The Send button is the discoverable path.
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void submit();
    }
  };

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <Controller
        name="message"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            multiline
            maxRows={6}
            size="small"
            placeholder="Ask Truv AI…"
            onKeyDown={handleKeyDown}
            sx={{
              '& .MuiOutlinedInput-root': {
                alignItems: 'center',
                gap: '8px',
                backgroundColor: 'common.white',
                border: '1px solid',
                borderColor: 'grey.300',
                borderRadius: '12px',
                padding: '10px 8px 10px 16px',
              },
              // Same as the design: the field lights up while focus is anywhere inside it.
              '&:focus-within .MuiOutlinedInput-root': { borderColor: colors.primary.bright },
              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
              '& .MuiInputBase-input': { padding: '3px 0', fontSize: '0.875rem', lineHeight: '20px' },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ margin: 0, height: 'auto' }}>
                  {isStreaming ? (
                    <IconButton onClick={onStop} aria-label="Stop generating" sx={sendButtonSx(false)}>
                      <CloseIcon width={16} height={16} />
                    </IconButton>
                  ) : (
                    // Fills in once there's something to send, so the button reads as armed.
                    <IconButton type="submit" disabled={!isValid} aria-label="Send message" sx={sendButtonSx(isValid)}>
                      <ArrowUpIcon width={20} height={20} />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
          />
        )}
      />
    </form>
  );
}
